import { useMemo } from 'react';
import { scoring } from '@tragfaehigkeit/core';
import { de } from '../../../lib/i18n/de';
import type { DimensionKey } from '../../hub/dimensions';
import { useWizardAudience } from '../WizardAudienceContext';
import {
  buildIdentityData,
  buildActivityData,
  buildStyleData,
  buildHealthData,
  buildPrivateData,
  type Fields,
} from '../../../lib/store/dimensionScoreInput';

/**
 * Kompakter Live-Score pro Freitext-Dimension (Analog zu ResourcesLiveScore).
 * Baut aus den Store-Feldern das Core-`XData` und ruft die Score-Engine.
 * Rein abgeleitet, keine Persistenz.
 */
function computeFor(
  dimension: DimensionKey,
  fields: Fields,
): { score: number; risks: readonly { severity: string; message: string }[] } {
  switch (dimension) {
    case 'identity': {
      const d = buildIdentityData(fields);
      return {
        score: scoring.computeIdentityScore(d),
        risks: scoring.getIdentityRiskIndicators(d),
      };
    }
    case 'activity': {
      const d = buildActivityData(fields);
      return {
        score: scoring.computeActivityScore(d),
        risks: scoring.getActivityRiskIndicators(d),
      };
    }
    case 'style': {
      const d = buildStyleData(fields);
      return { score: scoring.computeStyleScore(d), risks: scoring.getStyleRiskIndicators(d) };
    }
    case 'health': {
      const d = buildHealthData(fields);
      return { score: scoring.computeHealthScore(d), risks: scoring.getHealthRiskIndicators(d) };
    }
    case 'private': {
      const d = buildPrivateData(fields);
      // bgGapCents kommt aus dem Resources/BG-Modul — hier neutral (undefined).
      return { score: scoring.computePrivateScore(d), risks: scoring.getPrivateRiskIndicators(d) };
    }
    case 'resources':
      return { score: 0, risks: [] };
  }
}

const TRAFFIC_TONE: Record<string, string> = {
  green: 'bg-emerald-100 text-emerald-800',
  yellow: 'bg-amber-100 text-amber-800',
  red: 'bg-red-100 text-red-800',
};
const TRAFFIC_LABEL: Record<string, string> = {
  green: de.hub.liveScore.trafficGreen,
  yellow: de.hub.liveScore.trafficYellow,
  red: de.hub.liveScore.trafficRed,
};
const SEVERITY_TONE: Record<string, string> = {
  critical: 'text-red-700',
  warning: 'text-amber-700',
  info: 'text-stone-500',
};

export function DimensionLiveScore({
  dimension,
  fields,
}: {
  readonly dimension: DimensionKey;
  readonly fields: Fields;
}) {
  const audience = useWizardAudience();

  const { score, risks, light } = useMemo(() => {
    const r = computeFor(dimension, fields);
    // r.score ist ein ScoreValue (branded number); computeFor entbrandet auf number.
    const score = r.score as Parameters<typeof scoring.getTrafficLight>[0];
    return { ...r, light: scoring.getTrafficLight(score) };
  }, [dimension, fields]);

  // Mandanten-Sicht: KEIN Score, keine Ampel, keine Risiken. Der Score ist die
  // fachliche Bewertung des Beraters (Art. 22 DSGVO — keine scheinbar
  // automatisierte Ablehnung für den Mandanten). Neutrales Erfassungs-Signal.
  if (audience === 'mandant') {
    const hasContent = Object.values(fields).some((v) => v.trim().length > 0);
    return (
      <aside className="rounded-sm border-l-4 border-stone-200 bg-stone-50 px-5 py-4">
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {de.hub.liveScore.mandantSectionLabel}
        </p>
        <p className="mt-2 font-serif text-sm text-stone-700">
          {hasContent ? de.hub.liveScore.mandantRecorded : de.hub.liveScore.mandantEmpty}
        </p>
        {hasContent && (
          <p className="mt-1 font-serif text-xs text-stone-500">
            {de.hub.liveScore.mandantEvaluationNote}
          </p>
        )}
      </aside>
    );
  }

  const topRisks = risks.slice(0, 3);

  return (
    <aside className="rounded-sm border-l-4 border-stone-200 bg-stone-50 px-5 py-4">
      <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
        {de.hub.liveScore.sectionLabel}
      </p>
      <div className="mt-2 flex flex-wrap items-baseline gap-3">
        <span
          data-testid="live-score-value"
          className="font-serif text-4xl font-black tabular-nums text-stone-900"
        >
          {score}
        </span>
        <span className="font-sans text-xs uppercase tracking-widest text-stone-400">
          {de.hub.liveScore.ofLabel}
        </span>
        <span
          data-testid="live-score-light"
          className={`inline-flex items-center rounded-sm px-2 py-0.5 font-sans text-[10px] font-medium uppercase tracking-widest ${TRAFFIC_TONE[light] ?? ''}`}
        >
          {TRAFFIC_LABEL[light] ?? light}
        </span>
      </div>
      {topRisks.length > 0 && (
        <ul className="mt-3 space-y-1">
          {topRisks.map((r, i) => (
            <li
              key={i}
              className={`font-serif text-xs ${SEVERITY_TONE[r.severity] ?? 'text-stone-500'}`}
            >
              {r.message}
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}
